// Modern navbar styling — glass surface, pill-grouped user actions,
// refined tab interactions. The component contract is unchanged
// (Logo + Tabs + Notifications/Help + Avatar/Menu); this file only
// covers the visual layer.
//
// Design notes:
//   - Glassmorphic surface: translucent white + backdrop blur so the
//     bar floats over scrolling content. Falls back to ~opaque on
//     browsers without `backdrop-filter`.
//   - Env stripe (was 3px solid, brand-clashy yellow/green) is now a
//     2px gradient bar at the very top — still a dev/QA cue, far
//     calmer visually.
//   - User cluster (notifications, help, avatar) sits inside a single
//     rounded pill with a hairline separator. Modern app shells
//     (Linear, Vercel, Notion) all use this grouping treatment.
//   - Hover/active states are real now: every action has a subtle
//     brand-tinted hover wash with a 160ms transition.
//   - Loading bar runs a 3-stop primary gradient with a soft glow.

const envAccent = {
  localhost: 'linear-gradient(90deg, #F29C27, #FFB85C)',
  qa: 'linear-gradient(90deg, #1FA058, #4FC487)',
};

const getEnvAccent = () => {
  const env = Object.keys(envAccent).find((e) =>
    window.location.href.includes(e),
  );
  return env ? envAccent[env] : null;
};

export default (theme) => {
  const accent = getEnvAccent();
  const barHeight = theme.spacing(7); // 56px — modern roomier shell

  return {
    root: {
      // Glassmorphic surface
      backgroundColor: 'rgba(255, 255, 255, 0.72)',
      backdropFilter: 'saturate(180%) blur(20px)',
      WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      borderBottom: '1px solid rgba(26, 34, 49, 0.08)',
      boxShadow:
        '0 1px 0 rgba(26, 34, 49, 0.04), '
        + '0 8px 24px -16px rgba(11, 18, 32, 0.18)',
      // Env-indicator stripe sits on the AppBar's leading edge.
      ...(accent && {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: accent,
          pointerEvents: 'none',
          zIndex: 1,
        },
      }),
      marginBottom: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginBottom: 0,
      },
      // Safari / Firefox without backdrop-filter: keep readable.
      '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
      },
    },

    bar: {
      // Tight 5px lead so the logo sits flush with the page gutter
      // — `theme.spacing(3)` was leaving a heavy 24px void before
      // the mark.
      paddingLeft: '5px !important',
      paddingRight: theme.spacing(1.5),
      height: `${barHeight} !important`,
      minHeight: `${barHeight} !important`,
      gap: theme.spacing(1.5),
      [theme.breakpoints.down('md')]: {
        paddingLeft: '5px !important',
        paddingRight: theme.spacing(0.5),
        gap: theme.spacing(0.5),
      },
    },

    logo: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.primary.main,
      paddingRight: theme.spacing(2.5),
      marginRight: theme.spacing(1),
      // Hairline separator from the tab list. Slightly stronger than
      // before — the original 0.06α rule was effectively invisible
      // against the glass surface.
      borderRight: '1px solid rgba(26, 34, 49, 0.10)',
      [theme.breakpoints.down('md')]: {
        paddingRight: theme.spacing(1),
        marginRight: 0,
        borderRight: 'none',
      },
    },

    tabsSection: {
      flex: 1,
      minWidth: 0,
      // Vertically centre the strip within the 56px bar so tabs
      // sit on the same axis as the right-side user pill.
      display: 'flex',
      alignItems: 'center',
      // Vercel/Linear-style pill tabs: filled selected state, soft
      // hover wash, no MUI bottom indicator. Reads as a real nav
      // (matches the right-side user pill aesthetic) rather than
      // legacy "underline-on-bottom".
      '& .MuiTabs-indicator': { display: 'none' },
      // `align-items: center` (not the MUI default `stretch`) is what
      // lets the active pill be visibly taller than its neighbours —
      // otherwise flex-stretch would equalise every tab to the
      // tallest one's height.
      '& .MuiTabs-flexContainer': {
        alignItems: 'center',
        gap: theme.spacing(0.5),
      },
      // macOS-Dock-style magnification:
      //   - Active OR hovered tab  = baseline 13px
      //   - Direct neighbours of (active|hovered) = 12px (-1pt)
      //   - Everything else (the resting row) = 11px (-2pt)
      // Both directions of "neighbour" are needed: CSS3 gives us
      // `+` for the NEXT sibling only, so the PREVIOUS sibling
      // needs `:has(+ …)`. Both browsers we ship to (Chrome,
      // Safari 15.4+) support `:has()` since 2022.
      '& .MuiTab-root': {
        minHeight: 0,
        // 9×8 inactive pill (+2px on each axis vs the previous 7×6)
        // for a roomier resting row.
        padding: '9px 8px',
        margin: 0,
        borderRadius: 999,
        color: theme.palette.text.primary,
        fontWeight: 500,
        // Resting (far-from-active) size: baseline -2pt.
        fontSize: '0.6875rem',
        // Uppercase + wider tracking — reads as a real nav, less
        // like soft sentence-case copy.
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        transition: 'background-color 160ms ease, color 160ms ease, box-shadow 160ms ease, padding 160ms ease, font-size 180ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        '&:hover': {
          backgroundColor: 'rgba(0, 120, 255, 0.06)',
          color: theme.palette.primary.main,
          fontSize: '0.8125rem', // baseline on hover
        },
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 700,
          fontSize: '0.8125rem', // baseline on selected
          backgroundColor: 'rgba(0, 120, 255, 0.10)',
          boxShadow: '0 0 0 1px rgba(0, 120, 255, 0.18) inset',
          // ~+40% height vs the inactive pills (+2px each axis vs
          // the previous 14×12).
          padding: '16px 14px',
        },
        '&.Mui-selected:hover': {
          backgroundColor: 'rgba(0, 120, 255, 0.14)',
        },
      },
      // Direct neighbours of the active or hovered tab — one step up
      // from resting (-1pt vs baseline). `:not(.Mui-selected):not(:hover)`
      // is critical: without it, an active tab that happens to sit
      // next to a hovered one would get downsized by the neighbour
      // rule (cascade-order would beat the &.Mui-selected font-size).
      '& .MuiTab-root.Mui-selected + .MuiTab-root:not(.Mui-selected):not(:hover), & .MuiTab-root:not(.Mui-selected):not(:hover):has(+ .MuiTab-root.Mui-selected)': {
        fontSize: '0.75rem',
      },
      '& .MuiTab-root:hover + .MuiTab-root:not(.Mui-selected):not(:hover), & .MuiTab-root:not(.Mui-selected):not(:hover):has(+ .MuiTab-root:hover)': {
        fontSize: '0.75rem',
      },
      // Compact, integrated scroll arrows — default MUI renders them
      // as chunky IconButton circles that don't fit the pill row.
      '& .MuiTabs-scrollButtons': {
        width: 28,
        minWidth: 28,
        height: 28,
        marginInline: 2,
        borderRadius: 999,
        color: theme.palette.text.primary,
        opacity: 1,
        transition: 'background-color 160ms ease, color 160ms ease, opacity 160ms ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 120, 255, 0.06)',
          color: theme.palette.primary.main,
        },
        '&.Mui-disabled': { opacity: 0.18 },
        '& svg': { fontSize: 18 },
      },
    },

    userSection: {
      marginLeft: 'auto',
      // Pill container that visually groups every action on the right.
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0.5),
      borderRadius: 999,
      backgroundColor: 'rgba(26, 34, 49, 0.03)',
      border: '1px solid rgba(26, 34, 49, 0.05)',
      transition: 'background-color 160ms ease, border-color 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(26, 34, 49, 0.05)',
        borderColor: 'rgba(26, 34, 49, 0.08)',
      },
    },

    notificationsButton: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      // Hairline separator between actions and the avatar.
      paddingRight: theme.spacing(0.5),
      marginRight: theme.spacing(0.25),
      borderRight: '1px solid rgba(26, 34, 49, 0.08)',
      '& .MuiIconButton-root': {
        padding: 6,
        borderRadius: 8,
        color: theme.palette.text.primary,
        transition: 'background-color 160ms ease, color 160ms ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 120, 255, 0.08)',
          color: theme.palette.primary.main,
        },
        '& svg': { fontSize: 20 },
      },
      // Notification badge: keep brand color, tighten size.
      '& .MuiBadge-badge': {
        fontSize: 10,
        height: 16,
        minWidth: 16,
        padding: '0 4px',
      },
    },

    // The avatar wrapper is a MUI <Toolbar>; strip its minHeight and
    // turn it into a hover-pill so the avatar gets a tactile target
    // without leaking the Toolbar's geometry into our nav.
    userInfo: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
      padding: '2px',
      borderRadius: 999,
      minHeight: '0 !important',
      height: 'auto !important',
      transition: 'background-color 160ms ease, box-shadow 160ms ease',
      '&:hover': {
        backgroundColor: 'rgba(0, 120, 255, 0.08)',
      },
      '& .MuiAvatar-root': {
        width: 28,
        height: 28,
        fontSize: 13,
        fontWeight: 600,
        border: '2px solid rgba(255, 255, 255, 0.9)',
        boxShadow: '0 0 0 1px rgba(26, 34, 49, 0.08)',
        transition: 'box-shadow 160ms ease',
      },
      '&:hover .MuiAvatar-root': {
        boxShadow: '0 0 0 2px rgba(0, 120, 255, 0.4)',
      },
    },

    // Username row at the top of the avatar dropdown — small caps-ish
    // header with a subtle bottom rule.
    userName: {
      padding: theme.spacing(1, 2, 0.75),
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
      maxWidth: 220,
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: '0.01em',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      borderBottom: '1px solid rgba(26, 34, 49, 0.06)',
    },

    grow: { flexGrow: 1 },

    // Loading bar — 3px primary gradient anchored to the BOTTOM
    // edge of the AppBar (not the viewport top). Pre-fix this was
    // `position: fixed; top: 0; height: 2` which (a) merged into
    // the browser titlebar / preview-window chrome in embedded
    // environments — QA bug 9.1 — and (b) was thin enough that a
    // single dropped frame made it disappear entirely.
    //
    // Anchoring with `position: absolute; bottom: 0` keeps the bar
    // attached to the AppBar instead. Since the AppBar is
    // `position: sticky`, the bar follows it correctly on scroll
    // — and it now sits on the navbar's bottom border against the
    // page content underneath, so it reads clearly against any
    // background.
    linear: {
      zIndex: 3,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 3,
      backgroundColor: 'transparent',
    },
    colorPrimary: { backgroundColor: 'transparent' },
    barColorPrimary: {
      background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
      boxShadow: '0 0 8px rgba(0, 120, 255, 0.4)',
    },
  };
};
